import React, { Dispatch, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button as MuiButton, ButtonGroup, Typography } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { Button } from '../../../../../ui';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { toast } from '../../../../../ui/toast';
import {
  useCreateAutoOutstandingMutation,
  useCreateAutoSuitabilityMutation,
} from '../../../../../utils/api_service/jobOpeningsApi';
import {
  AutoEvaluation,
  JobDescription,
  showAllTypes,
} from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import Collapsible from '../../ui/CollapsibleChildren';
import { RangeSelectInput } from '../../ui/RangeSelectComponent';
import ListFields from './ListFieldComponents';

export interface AutoEvaluationTabProps {
  autoEvaluation: AutoEvaluation;
  setAutoEvaluation: Dispatch<React.SetStateAction<AutoEvaluation>>;
  evaluationType: 'Auto Suitability' | 'Auto Outstanding';
  importAutoSuitability?: () => void;
  completedElement: () => void;
  editable: boolean;
  jobDescription: JobDescription;
}

export const AutoEvaluationTab = ({
  autoEvaluation,
  setAutoEvaluation,
  evaluationType,
  importAutoSuitability,
  completedElement,
  editable,
  jobDescription,
}: AutoEvaluationTabProps) => {
  const { t } = useTranslation(NS.adminPages);
  const AE = ADMIN_PAGES_KEYS.jobOpenings.autoEvaluationTab;

  const rangeSelectOptions: SelectOptions[] = [
    { label: t(AE.greaterThan), value: 'Greater Than' },
    { label: t(AE.lesserThan), value: 'Lesser Than' },
  ];

  const [showAll, setShowAll] = useState<showAllTypes>('showAll');

  // RTK Mutations
  const [createAutoSuitabilityMutation, { isLoading: isSuitabilityLoading }] =
    useCreateAutoSuitabilityMutation();
  const [createAutoOutstandingMutation, { isLoading: isOutstandingLoading }] =
    useCreateAutoOutstandingMutation();

  const isSaving = isSuitabilityLoading || isOutstandingLoading;

  const saveAutoEvaluation = async () => {
    try {
      const data =
        evaluationType === 'Auto Suitability'
          ? await createAutoSuitabilityMutation(autoEvaluation).unwrap()
          : await createAutoOutstandingMutation(autoEvaluation).unwrap();

      setAutoEvaluation(data);
      toast.success(`${evaluationType} Saved Successfully`, {
        position: 'bottom-center',
      });
      completedElement();
    } catch (error) {
      toast.error(`${evaluationType} Save Failed`, {
        position: 'bottom-center',
      });
    }
  };

  const toggleShowAll = () => {
    let newShowAllState: showAllTypes = 'showAll';
    if (showAll === 'showAll') {
      newShowAllState = 'collapseAll';
    } else if (showAll === 'collapseAll') {
      newShowAllState = 'showAll';
    } else {
      newShowAllState = 'showAll';
    }
    setShowAll(newShowAllState);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%', position: 'relative' }}>
      {!editable && (
        <Alert
          severity='warning'
          sx={{
            borderRadius: 2,
            border: '1px solid #fef08a',
            bgcolor: '#fefce8',
            color: '#854d0e',
            '& .MuiAlert-icon': {
              color: '#ca8a04',
            },
          }}
        >
          <Typography variant='body2' component='span' sx={{ fontWeight: 600 }}>
            {t(AE.notEditable)}{' '}
          </Typography>
          <Typography variant='body2' component='span' sx={{ color: '#475569' }}>
            {t(AE.notEditableReason)}
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ButtonGroup
          size='small'
          color='primary'
          sx={{
            boxShadow: 'none',
            '& .MuiButton-root': {
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 600,
            },
          }}
        >
          <MuiButton
            onClick={() => setShowAll('showAll')}
            variant={showAll === 'showAll' ? 'contained' : 'outlined'}
            color='primary'
          >
            {t(AE.showAll)}
          </MuiButton>
          <MuiButton
            onClick={() => setShowAll('collapseAll')}
            variant={showAll === 'collapseAll' ? 'contained' : 'outlined'}
            color='primary'
          >
            {t(AE.collapseAll)}
          </MuiButton>
        </ButtonGroup>

        {evaluationType === 'Auto Outstanding' && (
          <Box>
            <Button
              onClick={importAutoSuitability || (() => {})}
              variant='primary'
              label={t(AE.importFromSuitability)}
              disabled={!editable}
            />
          </Box>
        )}
      </Box>

      <Box>
        <Collapsible
          title={t(AE.jobDescriptionDetails)}
          isOpen={showAll === 'showAll' || showAll === 'random'}
          toggleOpen={() => {
            if (showAll === 'random') {
              setShowAll('collapseAll');
            } else {
              toggleShowAll();
            }
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5 }}>
            <Box>
              <Typography sx={{ color: '#1e293b', py: 0.5, fontSize: 13, fontWeight: 500 }}>
                {t(AE.jobLocation)}
              </Typography>
              <TextInput
                name='Job Location'
                value={autoEvaluation.jobLocation}
                type='text'
                onChange={(event) => {
                  setAutoEvaluation({
                    ...autoEvaluation,
                    jobLocation: event.target.value,
                  });
                }}
                disabled={!editable}
              />
            </Box>
            <RangeSelectInput
              disabled={!editable}
              label={t(AE.age)}
              selectOptions={rangeSelectOptions}
              onSelectChange={(selected: { value: string }) =>
                setAutoEvaluation({
                  ...autoEvaluation,
                  age: {
                    ...autoEvaluation.age,
                    operator: selected.value as 'Greater Than' | 'Lesser Than',
                  },
                })
              }
              selectValue={
                rangeSelectOptions.find(
                  (option) => option.value === autoEvaluation.age.operator,
                ) || rangeSelectOptions[0]
              }
              onTextChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setAutoEvaluation({
                  ...autoEvaluation,
                  age: {
                    ...autoEvaluation.age,
                    value: parseInt(event.target.value, 10) || 0,
                  },
                });
              }}
              textValue={autoEvaluation.age.value.toString()}
              placeholder='Years'
              min='18'
              max='65'
            />
            <RangeSelectInput
              disabled={!editable}
              label={t(AE.workExperience)}
              selectOptions={rangeSelectOptions}
              onSelectChange={(selected: { value: string }) =>
                setAutoEvaluation({
                  ...autoEvaluation,
                  workExperience: {
                    ...autoEvaluation.workExperience,
                    operator: selected.value as 'Greater Than' | 'Lesser Than',
                  },
                })
              }
              selectValue={
                rangeSelectOptions.find(
                  (option) =>
                    option.value === autoEvaluation.workExperience.operator,
                ) || rangeSelectOptions[0]
              }
              onTextChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setAutoEvaluation({
                  ...autoEvaluation,
                  workExperience: {
                    ...autoEvaluation.workExperience,
                    value: parseInt(event.target.value, 10) || 0,
                  },
                });
              }}
              textValue={autoEvaluation.workExperience.value.toString()}
              placeholder='Years'
              min='0'
              max='50'
            />
          </Box>
        </Collapsible>
      </Box>

      <Box>
        <ListFields
          showAll={showAll}
          setShowAll={setShowAll}
          autoEvaluation={autoEvaluation}
          setAutoEvaluation={setAutoEvaluation}
          disabled={!editable}
          jobDescription={jobDescription}
          evaluationType={evaluationType}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, pt: 2.5, borderTop: '1px solid #f1f5f9' }}>
        <Button
          onClick={saveAutoEvaluation}
          variant='primary'
          label={`Save ${evaluationType}`}
          disabled={
            isSaving ||
            autoEvaluation.jobLocation === '' ||
            autoEvaluation.age.value === 0 ||
            autoEvaluation.educations === undefined ||
            autoEvaluation.certifications === undefined ||
            autoEvaluation.school === undefined ||
            autoEvaluation.skills === undefined ||
            autoEvaluation.keywords === undefined ||
            !editable
          }
        />
      </Box>
    </Box>
  );
};
